import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useAuthContext} from "../services/auth.service";
import {useDataContext} from "../services/data.service";
import useSWR, {mutate} from "swr";
import axios from "axios";
import {API_URL} from "../services/api.service";
import {toast} from "react-hot-toast";
import Button from "../components/_core/Button";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faUsers, faPlus, faMinus, faTrash, faKey, faPencil} from "@fortawesome/free-solid-svg-icons";

const UserManagementPage = () => {
    const {user, token} = useAuthContext();
    const {setModal, setModalOpen} = useDataContext();
    const navigate = useNavigate();

    const [showAddForm, setShowAddForm] = useState(false);
    const [newUsername, setNewUsername] = useState("");
    const [newName, setNewName] = useState("");
    const [newUserType, setNewUserType] = useState(1);
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const [editingUserId, setEditingUserId] = useState(null);
    const [editingUserType, setEditingUserType] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [bulkUserType, setBulkUserType] = useState(1);

    const USERS_URL = `${API_URL}/users`;

    const getUsers = () => axios.get(USERS_URL, {
        headers: {"Authorization": `Bearer ${token}`}
    }).then(res => res.data.users);

    const {data: users, error: usersError} = useSWR(token ? USERS_URL : null, getUsers);

    useEffect(() => {
        if (user && user.user_type !== 3) {
            navigate("/datasets");
        }
    }, [user, navigate]);

    useEffect(() => {
        document.title = "GT2 - User Management";
    }, []);

    if (usersError) {
        console.error(usersError);
    }

    const handleUserSelected = (userId) => {
        if (selectedUsers.includes(userId)) {
            setSelectedUsers(selectedUsers.filter(id => id !== userId));
        } else {
            setSelectedUsers([...selectedUsers, userId]);
        }
    };

    const handleSelectAll = () => {
        if (!users) return;
        if (selectedUsers.length === users.length) {
            setSelectedUsers([]);
        } else {
            setSelectedUsers(users.map(u => u.id));
        }
    };

    const handleBulkUpdateType = async () => {
        if (selectedUsers.length === 0) return;

        try {
            const response = await axios.patch(`${USERS_URL}/bulk-update`, {
                user_ids: selectedUsers,
                type: bulkUserType
            }, {
                headers: {"Authorization": `Bearer ${token}`}
            });

            if (response.data.success) {
                toast.success(`Updated ${response.data.updated_count} user(s) successfully!`, {position: "top-center"});
                setSelectedUsers([]);
                mutate(USERS_URL);
            } else {
                toast.error(response.data.error || "Failed to update users", {position: "top-center"});
            }
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to update users", {position: "top-center"});
        }
    };

    const handleBulkDelete = () => {
        if (selectedUsers.length === 0) return;

        // Check if user is trying to delete themselves
        if (selectedUsers.includes(user?.id)) {
            toast.error("Cannot delete your own account. Please deselect yourself first.", {position: "top-center"});
            return;
        }

        const selectedUserNames = users
            .filter(u => selectedUsers.includes(u.id))
            .map(u => u.name)
            .join(", ");

        setModal({
            title: "Delete Users",
            content: (
                <div>
                    <p className="mb-2">Are you sure you want to delete {selectedUsers.length} user(s)?</p>
                    <p className="text-sm text-gray-600">{selectedUserNames}</p>
                    <p className="mt-3 text-sm text-red-600 font-semibold">This action cannot be undone.</p>
                </div>
            ),
            okButtonText: "Delete",
            okButtonFn: async () => {
                try {
                    const response = await axios.post(`${USERS_URL}/bulk-delete`, {
                        user_ids: selectedUsers
                    }, {
                        headers: {"Authorization": `Bearer ${token}`}
                    });

                    if (response.data.success) {
                        toast.success(`Deleted ${response.data.deleted_count} user(s) successfully!`, {position: "top-center"});
                        setSelectedUsers([]);
                        mutate(USERS_URL);
                        setModalOpen(false);
                    } else {
                        toast.error(response.data.error || "Failed to delete users", {position: "top-center"});
                        setModalOpen(false);
                    }
                } catch (err) {
                    toast.error(err.response?.data?.error || "Failed to delete users", {position: "top-center"});
                    setModalOpen(false);
                }
            }
        });
        setModalOpen(true);
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const response = await axios.post(USERS_URL, {
                username: newUsername,
                name: newName,
                type: newUserType
            }, {
                headers: {"Authorization": `Bearer ${token}`}
            });

            if (response.data.success) {
                const createdName = newName;
                setNewUsername("");
                setNewName("");
                setNewUserType(1);
                setShowAddForm(false);
                mutate(USERS_URL);

                // Show the generated password in a modal
                setModal({
                    title: "User Created Successfully",
                    content: (
                        <div>
                            <p className="mb-3">The password for <strong>{createdName}</strong> is:</p>
                            <div className="bg-white border-2 border-slate-300 rounded p-3 font-mono text-lg text-center select-all">
                                {response.data.password}
                            </div>
                            <p className="mt-3 text-sm text-red-600 font-semibold">
                                This password will only be shown once. Please copy it now.
                            </p>
                        </div>
                    ),
                    okButtonText: "Done",
                    okButtonFn: () => setModalOpen(false)
                });
                setModalOpen(true);
            } else {
                toast.error(response.data.error || "Failed to create user", {position: "top-center"});
            }
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to create user", {position: "top-center"});
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteUser = async (userId) => {
        try {
            const response = await axios.delete(`${USERS_URL}/${userId}`, {
                headers: {"Authorization": `Bearer ${token}`}
            });

            if (response.data.success) {
                toast.success("User deleted successfully!", {position: "top-center"});
                setDeleteConfirmId(null);
                setSelectedUsers(selectedUsers.filter(id => id !== userId));
                mutate(USERS_URL);
            } else {
                toast.error(response.data.error || "Failed to delete user", {position: "top-center"});
            }
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to delete user", {position: "top-center"});
        }
    };

    const handleUpdateUserType = async (userId) => {
        try {
            const response = await axios.patch(`${USERS_URL}/${userId}`, {
                type: editingUserType
            }, {
                headers: {"Authorization": `Bearer ${token}`}
            });

            if (response.data.success) {
                toast.success("User type updated successfully!", {position: "top-center"});
                setEditingUserId(null);
                setEditingUserType(null);
                mutate(USERS_URL);
            } else {
                toast.error(response.data.error || "Failed to update user type", {position: "top-center"});
            }
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to update user type", {position: "top-center"});
        }
    };

    const handleResetPassword = (targetUser) => {
        setModal({
            title: "Reset Password",
            content: (
                <p>Are you sure you want to reset the password for <strong>{targetUser.name}</strong> ({targetUser.username})?</p>
            ),
            okButtonText: "Reset Password",
            okButtonFn: async () => {
                try {
                    const response = await axios.post(`${USERS_URL}/${targetUser.id}/reset-password`, {}, {
                        headers: {"Authorization": `Bearer ${token}`}
                    });

                    if (response.data.success) {
                        setModalOpen(false);
                        // Show the new password in a new modal
                        setModal({
                            title: "Password Reset Successfully",
                            content: (
                                <div>
                                    <p className="mb-3">The new password for <strong>{targetUser.name}</strong> is:</p>
                                    <div className="bg-white border-2 border-slate-300 rounded p-3 font-mono text-lg text-center select-all">
                                        {response.data.password}
                                    </div>
                                    <p className="mt-3 text-sm text-red-600 font-semibold">
                                        This password will only be shown once. Please copy it now.
                                    </p>
                                </div>
                            ),
                            okButtonText: "Done",
                            okButtonFn: () => setModalOpen(false)
                        });
                        setModalOpen(true);
                    } else {
                        toast.error(response.data.error || "Failed to reset password", {position: "top-center"});
                        setModalOpen(false);
                    }
                } catch (err) {
                    toast.error(err.response?.data?.error || "Failed to reset password", {position: "top-center"});
                    setModalOpen(false);
                }
            }
        });
        setModalOpen(true);
    };

    const startEditingType = (u) => {
        setEditingUserId(u.id);
        setEditingUserType(u.user_type);
    };

    const cancelEditingType = () => {
        setEditingUserId(null);
        setEditingUserType(null);
    };

    const getUserTypeName = (type) => {
        switch (type) {
            case 1: return "User";
            case 2: return "QA";
            case 3: return "Admin";
            default: return "Unknown";
        }
    };

    const loading = !users;
    const allSelected = users && users.length > 0 && selectedUsers.length === users.length;

    return (
        <div className="w-full h-full flex flex-col">
            <div className="flex justify-between items-end bg-slate-100 py-6 px-8">
                <h2 className="text-4xl font-bold">
                    <FontAwesomeIcon icon={faUsers} className="mr-2" />
                    User Management
                </h2>
                <div className="flex gap-4 items-center">
                    {!loading && <p className="text-gray-500 text-sm">{users.length} user(s)</p>}
                    <Button onClick={() => setShowAddForm(!showAddForm)}>
                        <FontAwesomeIcon icon={showAddForm ? faMinus : faPlus} className="mr-2" />
                        {showAddForm ? "Cancel" : "Add User"}
                    </Button>
                </div>
            </div>

            {showAddForm && (
                <div className="bg-slate-50 px-8 py-4 border-b border-slate-200">
                    <h3 className="text-lg font-semibold mb-4">Add New User</h3>
                    <form onSubmit={handleAddUser} className="flex flex-wrap gap-4 items-end">
                        <div className="flex flex-col">
                            <label className="text-sm text-gray-600 mb-1">Username</label>
                            <input
                                type="text"
                                value={newUsername}
                                onChange={(e) => setNewUsername(e.target.value)}
                                className="border border-gray-300 rounded px-3 py-2 w-48"
                                required
                            />
                        </div>
                        <div className="flex flex-col">
                            <label className="text-sm text-gray-600 mb-1">Display Name</label>
                            <input
                                type="text"
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                className="border border-gray-300 rounded px-3 py-2 w-48"
                                required
                            />
                        </div>
                        <div className="flex flex-col">
                            <label className="text-sm text-gray-600 mb-1">User Type</label>
                            <select
                                value={newUserType}
                                onChange={(e) => setNewUserType(parseInt(e.target.value))}
                                className="border border-gray-300 rounded px-3 py-2 w-32"
                            >
                                <option value={1}>User</option>
                                <option value={2}>QA</option>
                                <option value={3}>Admin</option>
                            </select>
                        </div>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? "Creating..." : "Create User"}
                        </Button>
                        <p className="text-sm text-gray-500 self-center">A password will be auto-generated</p>
                    </form>
                </div>
            )}

            {selectedUsers.length > 0 && (
                <div className="bg-blue-50 px-8 py-3 border-b border-blue-200 flex items-center gap-4">
                    <span className="text-blue-700 font-semibold">{selectedUsers.length} user(s) selected</span>
                    <div className="flex items-center gap-2">
                        <label className="text-sm text-blue-600">Set type to:</label>
                        <select
                            value={bulkUserType}
                            onChange={(e) => setBulkUserType(parseInt(e.target.value))}
                            className="border border-blue-300 rounded px-2 py-1 text-sm"
                        >
                            <option value={1}>User</option>
                            <option value={2}>QA</option>
                            <option value={3}>Admin</option>
                        </select>
                        <Button size="small" onClick={handleBulkUpdateType}>
                            Apply
                        </Button>
                    </div>
                    <div className="border-l border-blue-300 h-6" />
                    <Button size="small" onClick={handleBulkDelete}>
                        <FontAwesomeIcon icon={faTrash} className="mr-2" />
                        Delete Selected
                    </Button>
                    <div className="border-l border-blue-300 h-6" />
                    <Button size="small" style="muted" onClick={() => setSelectedUsers([])}>
                        Clear Selection
                    </Button>
                </div>
            )}

            <div className="flex items-center bg-slate-200 text-slate-500 text-sm uppercase tracking-wider font-bold justify-between px-8 py-3 top-0 shadow-lg z-10">
                <div className="w-8">
                    <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={handleSelectAll}
                        className="cursor-pointer"
                        title="Select all"
                    />
                </div>
                <div className="w-16">ID</div>
                <div className="w-48">Username</div>
                <div className="w-48">Name</div>
                <div className="w-32">Type</div>
                <div className="w-64 text-right"></div>
            </div>

            <div className="flex-grow overflow-y-auto py-4" id="users">
                <div className="flex flex-col gap-4">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                            <p className="text-lg">Loading users...</p>
                        </div>
                    ) : users.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                            <FontAwesomeIcon icon={faUsers} className="text-5xl mb-4 text-gray-300" />
                            <p className="text-lg">No users found</p>
                        </div>
                    ) : users.map(u => (
                        <div key={u.id} className={`flex items-center justify-between rounded px-4 py-2 mx-4 transition-all duration-150 hover:bg-slate-50 ${selectedUsers.includes(u.id) ? 'bg-blue-50 ring-1 ring-blue-300' : 'bg-slate-100'}`}>
                            <div className="w-8">
                                <input
                                    type="checkbox"
                                    checked={selectedUsers.includes(u.id)}
                                    onChange={() => handleUserSelected(u.id)}
                                    className="cursor-pointer"
                                />
                            </div>
                            <div className="w-16">{u.id}</div>
                            <div className="w-48">{u.username}</div>
                            <div className="w-48">{u.name}</div>
                            <div className="w-32">
                                {editingUserId === u.id ? (
                                    <select
                                        value={editingUserType}
                                        onChange={(e) => setEditingUserType(parseInt(e.target.value))}
                                        className="border border-gray-300 rounded px-2 py-1 text-sm"
                                    >
                                        <option value={1}>User</option>
                                        <option value={2}>QA</option>
                                        <option value={3}>Admin</option>
                                    </select>
                                ) : (
                                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                        u.user_type === 3 ? 'bg-purple-100 text-purple-700' :
                                        u.user_type === 2 ? 'bg-blue-100 text-blue-700' :
                                        'bg-gray-100 text-gray-700'
                                    }`}>
                                        {getUserTypeName(u.user_type)}
                                    </span>
                                )}
                            </div>
                            <div className="w-64 flex items-center justify-end gap-2">
                                {editingUserId === u.id ? (
                                    <>
                                        <Button size="small" onClick={() => handleUpdateUserType(u.id)}>
                                            Save
                                        </Button>
                                        <Button size="small" style="secondary" onClick={cancelEditingType}>
                                            Cancel
                                        </Button>
                                    </>
                                ) : deleteConfirmId === u.id ? (
                                    <>
                                        <Button size="small" onClick={() => handleDeleteUser(u.id)}>
                                            Confirm
                                        </Button>
                                        <Button size="small" style="secondary" onClick={() => setDeleteConfirmId(null)}>
                                            Cancel
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Button
                                            size="small"
                                            style="secondary"
                                            onClick={() => startEditingType(u)}
                                            title="Edit user type"
                                        >
                                            <FontAwesomeIcon icon={faPencil} />
                                        </Button>
                                        <Button
                                            size="small"
                                            style="secondary"
                                            onClick={() => handleResetPassword(u)}
                                            title="Reset password"
                                        >
                                            <FontAwesomeIcon icon={faKey} />
                                        </Button>
                                        <Button
                                            size="small"
                                            style="secondary"
                                            onClick={() => setDeleteConfirmId(u.id)}
                                            disabled={u.id === user?.id}
                                            title={u.id === user?.id ? "Cannot delete your own account" : "Delete user"}
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default UserManagementPage;
